import {commonWebSocket} from '@services/common/common.constants';
import {initWebsocket} from '@services/common/common.action';
import {getSelectedFamilyResource} from './user.utils';
import {createReducer} from 'redux-act';
import {
  appSettings,
  awaitReplyOnRequest,
  bootstrapTime,
  createUserSuccess,
  dismissErrorMessage,
  dismissShareModal,
  dismissSuccessMessage,
  displayErrorMessage,
  displaySuccessMessage,
  getCampaignTokenSuccess,
  getInviteTokenSuccess,
  loginSuccess,
  onboardingSetActiveStep,
  reportWebsocketErrorToUser,
  getMunicipalitySuccess,
  seenHelp,
  selectLanguage,
  storeAppToken,
  dismissHelpNotice,
  unDismissHelpNotice,
  setShowWeekNum,
  setOnlyWeeksOf,
  setShowActivitiesInEditMode,
  enhancedTextSize,
  setEnhancedVisibility,
  setDarkMode
} from './user.actions';
import TranslateNew from '@translate/TranslateNew';
import { IEcoSplitSetting } from '@app-types/websocket/economy.types';
import { IFamilyMember } from '@app-types/websocket/family.types';
import { strict } from 'assert';
import { auth } from 'firebase';
import { toxicCheckLevelOf } from './toxicCheckLevel';

interface IState {
  language: string,
  session: {
    bootstrapTime: number,
    selectedFamily: number,
    inviteToken: string,
    data: {
      id?: number,
      authorization: string | undefined,
      resources?: any[],
    },
    extendedFamily:[],
  },
  dismissedHelpNotices:string[],
  help:{
    schedules:boolean,
    chat:boolean,
    experts:boolean,
    createEvent:boolean,
    todos:boolean,
    createSuggestion:boolean,
    mySuggestion:boolean,
    respondSuggestion:boolean,
  },
  appSettings : {
    calendarHeight:number,
    calendarSize:string,
  },
  appToken?: string,

  showWeekNum:boolean,
  showActivitiesInEditMode:boolean,

  calendarSettings : {
    accountId?: number,
    familyId?: number,
    emailToken?: string | null,
    exportTodos:boolean,
    exportEvents:boolean,
    exportBreaks:boolean,
    notifyHandovers:boolean,
    onlyWeeksOf?:number|null,
    notifyActivities:boolean,
    muteListNotifications:boolean,
    emailMessages:boolean,
    calendars:any[],
    synchCalendar:boolean,
    excludeCalendar:any[],
    holidaysCountry: any,
    familyColors:any[],
    allowCalls:boolean,
  },
  onboarding: {
    data: any,
    meta: {
      activeStep: number
    }
  },
  sentRequest: string,
  didReconnect: boolean,

  
  successMessage: {
    message: string,
    dismissed: boolean,
  },
  
  errorMessage: {
    message: string,
    dismissed: boolean,
    connection:boolean,
    id:number
  },

  currency:string,
  swishF1:string,
  swishF2:string,
  venmoF1:string,
  venmoF2:string,
  cashappF1:string,
  cashappF2:string,
  paypalF1:string,
  paypalF2:string,
  paymentMethod:string,
  strictToxicCheck:boolean,
  // 0 normal, 1 strict, 2 very strict. Undefined in state persisted before the server sent it.
  toxicCheckLevel?:number,
  // For the connected family. Off until its snapshot says otherwise; a family switch resets it.
  autoApproveSuggestions:boolean,
  enhancedTextSize:boolean,
  enhancedVisibility:boolean,
  // Kept on the account: true always dark, false always light, undefined follows the phone or browser.
  darkMode?:boolean,
  // A dark mode chosen on this device before the setting moved to the account, not saved there yet.
  darkModeUnsaved?:boolean,
};

const defaultState:IState= {
  language: "",
  session: {
    bootstrapTime: 0,
    selectedFamily: 0,
    inviteToken: "",
    data: {
      authorization: "",
    },
    extendedFamily:[],
  },
  dismissedHelpNotices:["test"],
  help:{
    schedules:false,
    chat:false,
    experts:false,
    createEvent:false,
    todos:false,
    createSuggestion:false,
    mySuggestion:false,
    respondSuggestion:false,
  },
  appSettings : {
    calendarHeight:350,
    calendarSize:"large",
  },
  appToken: '',
  calendarSettings : {
    exportTodos:true,
    exportEvents:true,
    exportBreaks:true,
    notifyHandovers:true,
    notifyActivities:true,
    muteListNotifications:false,
    emailMessages:true,
    calendars:[],
    synchCalendar:false,
    excludeCalendar:[],
    holidaysCountry: undefined,
    familyColors:[],
    allowCalls:true,
  },
  onboarding: {
    data: {

    },
    meta: {
      activeStep: 0
    }
  },
  sentRequest: '',
  didReconnect: false,
  errorMessage: {
    message: '',
    dismissed: true,
    connection:false,
    id:0
  },
  showWeekNum:false,
  showActivitiesInEditMode:false,
  successMessage: {
    message: '',
    dismissed: true,
  },
  currency:"SEK",
  swishF1:"",
  swishF2:"",
  venmoF1:"",
  venmoF2:"",
  cashappF1:"",
  cashappF2:"",
  paypalF1:"",
  paypalF2:"",
  paymentMethod:"SWISH",
  strictToxicCheck:false,
  autoApproveSuggestions:false,
  enhancedTextSize:false,
  enhancedVisibility:false,
};

const reducer = createReducer<IState>({}, defaultState);

reducer

  .on(selectLanguage, (state, payload) => {
    const tn:TranslateNew = TranslateNew.getInstance();
    tn.setLanguage(payload);
    return {
      ...state,
      language: payload,
    };
  })
  .on(appSettings, (state, payload) => {
    return {
      ...state,
      appSettings: {
        ...state.appSettings,
        ...payload,
      }
    };
  })
.on(commonWebSocket.special.USER_TOKEN_UPDATE, (state:IState, payload: any) => {
  const token = payload?.userToken;

  // basic validation
  if (typeof token !== "string" || token.length < 20) return state;

  // optional: ignore if same token
  if (state.session?.data?.authorization === token) return state;

  return {
    ...state,
    session: {
      ...state.session,
      data: {
        ...(state.session?.data ?? {}),
        authorization: token,
        // optional: track when updated
        tokenUpdatedAt: Date.now(),
      },
    },
  };
})
  .on(initWebsocket, (state) => ({
    ...state,
    calendarSettings: {...state.calendarSettings, accountId: undefined, familyId: undefined, emailToken: undefined},
  }))
  .on(commonWebSocket.replace.CALENDAR_SETTINGS, (state, payload:any) => {
    if (!payload?.calendarSettings || typeof payload.calendarSettings !== "object") {
      return state;
    }
    const settings = payload.calendarSettings;
    if ((settings.accountId !== undefined && settings.accountId !== state.session.data.id)
      || (settings.familyId !== undefined && settings.familyId !== getSelectedFamilyResource(state.session)?.id)) {
      return state;
    }
    // Merge over the previous settings so missing keys keep a usable value, and
    // force the list fields to arrays — the server can serialize them as null.
    const merged = {
      ...state.calendarSettings,
      ...payload.calendarSettings,
    };
    return {
      ...state,
      calendarSettings: {
        ...merged,
        calendars: Array.isArray(merged.calendars) ? merged.calendars : [],
        excludeCalendar: Array.isArray(merged.excludeCalendar) ? merged.excludeCalendar : [],
        familyColors: Array.isArray(merged.familyColors) ? merged.familyColors : [],
      },
    };
  })
  .on(commonWebSocket.snapshot.RESOURCE_SNAPSHOT, (state, payload:any) => {
    const resources:any[] = Array.isArray(payload?.resources) ? payload.resources : [];
    const currentSelectedFamily:number = Number.isInteger(state.session?.selectedFamily)
      ? state.session.selectedFamily
      : 0;
    const previousResources:any[] = Array.isArray(state.session?.data?.resources)
      ? state.session.data.resources
      : [];
    const previousSelectedResource = currentSelectedFamily >= 0
      ? previousResources[currentSelectedFamily]
      : undefined;

    let selectedFamily = 0;
    if (resources.length > 0) {
      const previousResourceId = previousSelectedResource?.id;
      if (previousResourceId !== undefined) {
        const matchingIndex = resources.findIndex((resource:any) => resource?.id === previousResourceId);
        selectedFamily = matchingIndex >= 0
          ? matchingIndex
          : (currentSelectedFamily >= 0 && currentSelectedFamily < resources.length ? currentSelectedFamily : 0);
      } else {
        selectedFamily = currentSelectedFamily >= 0 && currentSelectedFamily < resources.length
          ? currentSelectedFamily
          : 0;
      }
    }

    return {
      ...state,
      session: {
        ...state.session,
        selectedFamily,
        data: {
          ...state.session.data,
          resources,
        },
      },
    };
  })
  .on(commonWebSocket.special.REQUEST_ID, (state:IState, payload:any) => {

    if (state.sentRequest && state.sentRequest !== '' && payload
      && payload.requestId && state.sentRequest === payload.requestId) {
      const tn:TranslateNew = TranslateNew.getInstance();
      return {
        ...state,
        successMessage:{message:tn.lang.sent, dismissed:false},
        sentRequest: '',
      };
    } else if (payload.requestId === 'LOGIN' && !payload.hasError && state.didReconnect){
      return {
        ...state,
        errorMessage:{dismissed:true, message: '', connection:false, id: Date.now()},
        successMessage:{message:"Connected!", dismissed:true},
      };
    } else {
      return {
        ...state,
      };
    }
  })
  .on(commonWebSocket.replace.STRICT_TOXIC_CHECK, (state, payload:any) => {
    return {
      ...state,
      strictToxicCheck: !!payload.strictToxicCheck,
      toxicCheckLevel: toxicCheckLevelOf(payload),
    };
  })
  .on(commonWebSocket.replace.AUTO_APPROVE_SUGGESTIONS, (state, payload:any) => {
    return {
      ...state,
      autoApproveSuggestions: payload.autoApproveSuggestions === true,
    };
  })
  .on(commonWebSocket.replace.DARK_MODE, (state, payload:any) => {
    const darkMode = typeof payload.darkMode === 'boolean' ? payload.darkMode : undefined;
    // Until the account has a choice of its own, its "none" must not undo one made on this device
    // before the setting moved there. saveUnsavedDarkMode is saving that one.
    if (state.darkModeUnsaved && darkMode === undefined) {
      return state;
    }
    return {
      ...state,
      darkMode,
      darkModeUnsaved: false,
    };
  })
  .on(dismissErrorMessage, (state, payload) => {
    return {
      ...state,
      errorMessage: {
        ...state.errorMessage,
        dismissed: true,
      },
    };
  })
  .on(dismissSuccessMessage, (state, payload) => {
    return {
      ...state,
      successMessage: {
        ...state.successMessage,
        dismissed: true,
      },
    };
  })
  .on(seenHelp, (state, payload) => {
    if (!payload || payload.length < 2) { return state; }
    return {
      ...state,
      help: {
        ...state.help,
        [payload]: true,
      },
    };
  })
  .on(enhancedTextSize, (state, payload:boolean) => {

    return {
      ...state,
      enhancedTextSize: payload,
    };
  })
  .on(setEnhancedVisibility, (state, payload:boolean) => {

    return {
      ...state,
      enhancedVisibility: payload,
    };
  })
  .on(setDarkMode, (state, payload:boolean|undefined) => {
    return {
      ...state,
      darkMode: payload,
      darkModeUnsaved: false,
    };
  })
  .on(setShowWeekNum, (state, payload:boolean) => {

    return {
      ...state,
      showWeekNum: payload,
    };
  })
  // Shown straight away; the server confirms with a calendar settings snapshot.
  .on(setOnlyWeeksOf, (state, payload:number) => ({
    ...state,
    calendarSettings: {...state.calendarSettings, onlyWeeksOf: payload > 0 ? payload : null},
  }))
  .on(setShowActivitiesInEditMode, (state, payload:boolean) => {

    return {
      ...state,
      showActivitiesInEditMode: payload,
    };
  })
  .on(dismissHelpNotice, (state:IState, payload:string) => {

    const newDismissed:string[] =  (state.dismissedHelpNotices)?[...state.dismissedHelpNotices]:[];
    if (!newDismissed.includes(payload)) {
      newDismissed.push(payload);
    }

    return {
      ...state,
      dismissedHelpNotices: newDismissed,
    };
  })
  .on(unDismissHelpNotice, (state, payload:string) => {

    let newDismissed:string[] =  (state.dismissedHelpNotices)?[...state.dismissedHelpNotices]:[];
    newDismissed = newDismissed.filter((noti) => noti !== payload);

    return {
      ...state,
      dismissedHelpNotices: newDismissed,
    };
  })
  .on(commonWebSocket.special.ERROR_MESSAGE, (state, payload:any) => {

    return {
      ...state,
      errorMessage: {
        dismissed: false,
        message: typeof payload.errorMsg === "string" ? payload.errorMsg : "",
        connection: false,
        id: Date.now(),
      },
    };
  })
  .on(displayErrorMessage, (state, payload:string) => {

    return {
      ...state,
      errorMessage: {
        dismissed: false,
        message: payload,
        connection: false,
        id: Date.now(),
      },
    };
  })
  .on(reportWebsocketErrorToUser, (state, payload) => {
    return {
      ...state,
      didReconnect: true,
      errorMessage: {
        dismissed: false,
        message: payload,
        connection: true,
        id: Date.now(),
      },
    };
  })
  .on(displaySuccessMessage, (state, payload:string) => {
    return {
      ...state,
      successMessage: {
        dismissed: false,
        message: payload,
      },
    };
  })
  .on(loginSuccess, (state, payload:any) => {
    return {
      ...state,
      session: {
        ...state.session,
        data: payload,
      },
    };
  })
  .on(awaitReplyOnRequest, (state, payload:string) => {
    return {
      ...state,
      sentRequest: payload,
    };
  })
  .on(createUserSuccess, (state, payload:any) => {
    // Invite lookups arrive without a token; never let one log the current user out.
    const currentAuth = state.session?.data?.authorization;
    if (typeof currentAuth === "string" && currentAuth.length > 0 && !payload?.authorization) {
      return state;
    }
    return {
      ...state,
      session: {
        ...state.session,
        data: payload,
      },
    };
  })
  .on(getMunicipalitySuccess, (state, payload:any) => {
    return {
      ...state,
      session: {
        ...state.session,
      },
    };
  })
  .on(getInviteTokenSuccess, (state, payload:any) => {
    return {
      ...state,
      session: {
        ...state.session,
        inviteToken: payload,
      },
    };
  })
  .on(getCampaignTokenSuccess, (state, payload:any) => {
    return {
      ...state,
      session: {
        ...state.session,
        campaigns: payload,
      },
    };
  })
  .on(bootstrapTime, (state, payload) => {
    return {
      ...state,
      session: {
        ...state.session,
        bootstrapTime: payload,
      },
    };
  })
  .on(onboardingSetActiveStep, (state, payload) => {
    return {
      ...state,
      onboarding: {
        ...state.onboarding,
        activeStep: payload
      },
    };
  })
  
  .on(storeAppToken, (state, payload:any) => {
    return {
      ...state,
      appToken: payload,
    };
  })
  .on(dismissShareModal, (state, payload) => {
    return {
      ...state,
      dismissShareModal: true,
    };
  })
  .on(commonWebSocket.snapshot.FAMILY_SNAPSHOT, (state:any, payload:any) => {
    if (!Array.isArray(payload?.family)) {
      return state;
    }

    let completeList:IFamilyMember[] = [];
    if (Array.isArray(state.session?.extendedFamily)) {
      completeList = state.session.extendedFamily.filter((mem: any) => mem && typeof mem === "object");
    }

    for (const newMember of payload.family) {
      if (!newMember || typeof newMember !== "object" || newMember.id === undefined) {
        continue;
      }
      completeList = completeList.filter((mem) => mem.id !== newMember.id);
      completeList.push(newMember);
    }

    return {
      ...state,
      session:{...state.session, extendedFamily:completeList}
    };
  })
  .on(commonWebSocket.snapshot.ECO_SETTINGS_SNAPSHOT, (state: any, payload: any) => {
    if (!Array.isArray(payload?.ecoSettings)) {
      return state;
    }
    const userLocale = navigator.language || "sv-SE";
    let swishF1:string = "";
    let swishF2:string = "";
    let venmoF1:string = "";
    let venmoF2:string = "";
    let cashappF1:string = "";
    let cashappF2:string = "";
    let paypalF1: string = "";
    let paypalF2: string = "";
    

    // TODO baserat på navigator sett dessa
    let currency:string = "SEK";
    let paymentMethod = "SWISH";

    if (userLocale === "en-US") {
      currency = "USD";
      paymentMethod = "CASHAPP";
  }
    const settings:IEcoSplitSetting[] = [];
    for (const data of payload.ecoSettings) {
      if (!data) {
        continue;
      }
      if (typeof data.transferIdOne === "string") {
        if (data.accountIdOne < data.accountIdTwo) {
          swishF1 = data.transferIdOne;
        } else {
          swishF2 = data.transferIdOne;
        }
      }
      if (typeof data.transferIdTwo === "string") {
        if (data.accountIdOne < data.accountIdTwo) {
          swishF2 = data.transferIdTwo;
        } else {
          swishF1 = data.transferIdTwo;
        }
      }
      if (typeof data.venmoOne === "string") {
        if (data.accountIdOne < data.accountIdTwo) {
          venmoF1 = data.venmoOne;
        } else {
          venmoF2 = data.venmoOne;
        }
      }
      if (typeof data.venmoTwo === "string") {
        if (data.accountIdOne < data.accountIdTwo) {
          venmoF2 = data.venmoTwo;
        } else {
          venmoF1 = data.venmoTwo;
        }
      }
      if (typeof data.cashappOne === "string") {
        if (data.accountIdOne < data.accountIdTwo) {
          cashappF1 = data.cashappOne;
        } else {
          cashappF2 = data.cashappOne;
        }
      }
      if (typeof data.cashappTwo === "string") {
        if (data.accountIdOne < data.accountIdTwo) {
          cashappF2 = data.cashappTwo;
        } else {
          cashappF1 = data.cashappTwo;
        }
      }

      if (typeof data.paypalOne === "string") {
        if (data.accountIdOne < data.accountIdTwo) {
          paypalF1 = data.paypalOne;
        } else {
          paypalF2 = data.paypalOne;
        }
      }
      if (typeof data.paypalTwo === "string") {
        if (data.accountIdOne < data.accountIdTwo) {
          paypalF2 = data.paypalTwo;
        } else {
          paypalF1 = data.paypalTwo;
        }
      }

      if (typeof data.currency === "string") {
        currency = data.currency;
      }

      if (typeof data.paymentMethod === "string") {
        paymentMethod = data.paymentMethod;
      }

      const setting:IEcoSplitSetting = {  
        start:data.startTime,
        split:[],
      }
      setting.split.push({  
        accountId:data.accountIdOne,
        percentage:data.percentageIdOne});
      setting.split.push({  
        accountId:data.accountIdTwo,
        percentage:data.percentageIdTwo});
      settings.push(setting);
    }
    
    return {
      ...state,
      currency,
      swishF1,
      swishF2,
      venmoF1,
      venmoF2,
      cashappF1,
      cashappF2,
      paypalF1,
      paypalF2,
      paymentMethod,
      splitSetting:settings,
    };
  });


export default reducer;



// WEBPACK FOOTER //
// ./src/services/user/user.reducer.ts